import React, { useState } from 'react';
import styled from 'styled-components';
import { H4, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IOnboardingEmployee } from 'types';
import { formatJobTitle, sortAlphaNumerically } from 'utils';

const Layout = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: start;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)};
  width: 100%;

  h4.title {
    font-weight: ${theme.font.weight.semiBold};
  }

  p.description {
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }
`;

const DetailsButton = styled.button`
  color: ${theme.color.primary[600]};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  position: absolute;
  right: ${toRem(16)};
`;

const List = styled.div`
  display: block;
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(theme.space.md)};
  border-radius: ${toRem(8)};
  margin-top: ${toRem(theme.space.md)};
  width: 100%;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.sm)};
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

const Row = styled.div`
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};

  width: 100%;

  p {
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: normal;

    &.grey {
      color: ${theme.color.neutral[500]};
    }

    &.status {
      margin-top: ${toRem(theme.space.sm)};
      font-style: italic;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(theme.space.xs)};
`;

interface IOnboardingSummaryListItemProps {
  title: string;
  description: string;
  employees: IOnboardingEmployee[];
  dataCy?: string;
}

export const OnboardingSummaryListItem: React.FC<IOnboardingSummaryListItemProps> = ({ title, description, employees, dataCy = 'onboarding-summary-list-item' }) => {
  const [showDetails, setShowDetails] = useState(false);
  const onToggleDetails = () => setShowDetails(!showDetails);

  return (
    <Layout data-cy={dataCy}>
      <H4 className="title" data-cy={`${dataCy}-title`}>
        {title}
      </H4>
      <Text size="small" className="description" data-cy={`${dataCy}-description`}>
        {description}
      </Text>
      <DetailsButton data-cy={`${dataCy}-details-button`} onClick={onToggleDetails}>
        View Details
      </DetailsButton>
      {showDetails && (
        <List>
          {[...employees].sort(sortAlphaNumerically('firstName', 'lastName')).map((employee) => (
            <ListItem key={employee.providerId}>
              <Row data-cy={`${dataCy}-employee-row`}>
                <Wrapper>
                  <Text size="small" fontWeight="semibold" data-cy={`${dataCy}-employee-name`}>
                    {employee.firstName} {employee.lastName}
                  </Text>
                  <Text size="xsmall" className="grey" data-cy={`${dataCy}-employee-email`}>
                    {employee.inviteRecipient}
                  </Text>
                </Wrapper>
                <Wrapper>
                  <Text size="xsmall" data-cy={`${dataCy}-employee-job-title`}>
                    {formatJobTitle(employee.job.title)}
                  </Text>
                </Wrapper>
              </Row>
              {employee.onboard.reason && (
                <Row data-cy={`${dataCy}-employee-reason-row`}>
                  <Text size="xsmall" className="status" data-cy={`${dataCy}-employee-reason-value`}>
                    {employee.onboard.reason}
                  </Text>
                </Row>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Layout>
  );
};
